import { LBButton } from '@linearb/buzz';
import { WarningBox } from '@linearb/linui-common';
import { Stack } from '@mui/material';
import { useMemo } from 'react';
import {
  getCanReauth,
  getDataSensors,
  getNotificationsSensors
} from './multiple-connectivity-issues-bar.service';
import SensorCTA from './SensorCTA';
import { useSelector } from 'react-redux';
import { myAccountSelector } from 'state/selectors/account';
import { startCase } from 'lodash';

const getBarText = ({
  failingSensorsAuthorizations,
  rateLimitSensorsAuthorizations,
  account
}) => {
  const notificationSensors = getNotificationsSensors(
    failingSensorsAuthorizations
  );
  const dataSensors = getDataSensors(failingSensorsAuthorizations);
  const issueSensors = [
    ...failingSensorsAuthorizations,
    ...rateLimitSensorsAuthorizations
  ];

  if (issueSensors.length > 1) {
    if (notificationSensors.length && dataSensors.length) {
      return 'Your data may be out of sync and you’re not getting notifications: multiple integration issues detected';
    } else if (notificationSensors.length) {
      return "You're not getting notifications: multiple integration issues detected";
    } else if (dataSensors.length || rateLimitSensorsAuthorizations.length) {
      return 'Your data may be out of sync: multiple integration issues detected';
    }
  }

  let preText;
  let sensorAuthorization;
  if (notificationSensors?.length === 1) {
    sensorAuthorization = notificationSensors[0];
    preText = `You are not getting notifications:`;
  } else if (dataSensors?.length === 1) {
    sensorAuthorization = dataSensors[0];
    preText = `Your data is out of sync:`;
  } else if (rateLimitSensorsAuthorizations?.length === 1) {
    const rateLimitSensor = rateLimitSensorsAuthorizations[0];
    return rateLimitSensor.notification.description;
  }

  const integrationName = sensorAuthorization?.name;
  preText = `${preText} ${integrationName} `;

  if (sensorAuthorization?.is_multi_token_enabled) {
    return `${preText} is not connected. Please reach out customer support`;
  }
  if (getCanReauth(sensorAuthorization, account)) {
    return `${preText} is not connected`;
  }

  const providerName = startCase(sensorAuthorization?.sensor_name);
  return `${preText} (${providerName}) is not connected, please contact your organization's admin.`;
};

const MultipleConnectivityTitle = ({
  failingSensorsAuthorizations,
  rateLimitSensorsAuthorizations,
  toggle,
  isExpanded
}) => {
  const account = useSelector(myAccountSelector);
  const issueSensors = useMemo(
    () => [...failingSensorsAuthorizations, ...rateLimitSensorsAuthorizations],
    [failingSensorsAuthorizations, rateLimitSensorsAuthorizations]
  );

  const getButton = () => {
    if (issueSensors.length > 1) {
      return (
        <LBButton
          data-cy='view-hide-multiple-connectivity-issues-button'
          data-testid='view-hide-multiple-connectivity-issues-button'
          variant='outline'
          onClick={toggle}
        >
          {isExpanded ? 'Hide' : 'View'}
        </LBButton>
      );
    }

    if (failingSensorsAuthorizations?.length) {
      const sensorAuthorization = failingSensorsAuthorizations[0];
      if (getCanReauth(sensorAuthorization, account)) {
        return (
          <SensorCTA
            includeLogo={true}
            sensorAuthorization={sensorAuthorization}
          />
        );
      }
    } else if (rateLimitSensorsAuthorizations?.length) {
      const rateLimitSensor = rateLimitSensorsAuthorizations[0];
      return rateLimitSensor.notification.cta;
    }
  };

  const barText = useMemo(() => {
    return getBarText({
      failingSensorsAuthorizations,
      rateLimitSensorsAuthorizations,
      account
    });
  }, [failingSensorsAuthorizations, rateLimitSensorsAuthorizations]);

  return (
    <WarningBox
      action={
        <Stack height='100%' justifyContent='center'>
          {getButton()}
        </Stack>
      }
    >
      {barText}
    </WarningBox>
  );
};

export default MultipleConnectivityTitle;
